<template>
  <div
    class="top_header_con"
    :class="{
      'top_header_con--toolbar-only': toolbarOnly,
      'top_header_con--with-fullscreen': showFullscreenButton,
      'top_header_con--light-icons': lightToolbarIcons
    }"
    :style="{ ...borderStyle, ...homeHeightStyle }"
  >
    <div v-if="!toolbarOnly" class="left">
      <slot name="logo"></slot>
      <slot name="menu"></slot>
    </div>
    <div class="right">
      <div class="time">{{ currentTime }}</div>
      <SvgIcon
        v-if="showDpButton"
        @click="goBigScreen"
        icon-class="Dp"
        :style="toolbarIconStyle(24)"
        v-hasPermi="['overall:overview:bigScreen']"
      ></SvgIcon>
      <LangSelector :light-icon="lightToolbarIcons" />
      <SvgIcon
        v-if="showFullscreenButton && isFullscreen"
        class="top-header-fullscreen"
        icon-class="exit-fullscreen"
        :style="toolbarIconStyle(22)"
        @click="toggleFullscreen"
      />
      <el-icon v-else-if="showFullscreenButton" class="top-header-fullscreen" :style="toolbarIconStyle(22)" @click="toggleFullscreen">
        <FullScreen />
      </el-icon>
      <el-badge v-if="showNotification" is-dot :hidden="notifyUnreadCount <= 0">
        <SvgIcon @click="showNotify = !showNotify" icon-class="homeMessage" :style="toolbarIconStyle(22)"></SvgIcon>
      </el-badge>
      <el-dropdown v-if="showUserMenu" popper-class="dark-dropdown" class="right-menu-item hover-effect" @command="handleCommand">
        <img :src="userStore.avatar"
          style="width: 30px; height: 30px; border-radius: 50%; cursor: pointer; outline: none" />
        <template #dropdown>
          <el-dropdown-menu>
            <!-- <router-link to="/user/profile"> -->
            <el-dropdown-item command="profile">{{ $t('navbar.personalCenter') }}</el-dropdown-item>
            <!-- </router-link> -->
            <!-- <router-link v-for="value in menuArr" :to="value.route">
              <el-dropdown-item>{{ value.name }}</el-dropdown-item>
            </router-link> -->
            <router-link to="/system/user" v-if="hasSystemMenu">
              <el-dropdown-item>{{ $t('navbar.systemManagement') }}</el-dropdown-item>
            </router-link>
            <el-dropdown-item divided command="logout">
              <span>{{ $t('navbar.logout') }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <div v-if="showNotification" style="position: fixed;top: 60px;right: 10px;">
      <Notify :show="showNotify" @close="showNotify = false" @unread-change="handleNotifyUnreadChange"></Notify>
    </div>
    <IModal v-if="showUserMenu" v-model="showProfile" position="center">
      <ProfileModal @close="showProfile = false"></ProfileModal>
    </IModal>
  </div>
</template>
<script setup lang="ts">
import '@/assets/styles/dark.scss';
import { parseTime } from '@/utils/ruoyi';
import useUserStore from '@/store/modules/user';
import { useI18n } from 'vue-i18n';
import usePermissionStore from '@/store/modules/permission';
import { FullScreen } from '@element-plus/icons-vue';
import LangSelector from '@/components/LangSelect/LangSelector.vue';
import ProfileModal from './profileModal.vue';
import Notify from './Notify.vue';
import { initWebSocket } from '@/utils/websocket.js';
import emitter from '@/utils/eventBus';
import { EVENT_GLOBAL_NOTICE_RECEIVED, normalizeNoticePayload } from './notice-adapter';
import { getTenantLoginUrl } from '@/utils/tenantRoute';
const showProfile = ref(false);
const permissionStore = usePermissionStore();
const userStore = useUserStore();
const { t } = useI18n();
/**
 * 是否展示「系统管理」入口：以顶栏路由（后端菜单树 topbarRouters）中是否存在 /system 菜单为准，
 * 不再依赖角色名判断，避免后端调整角色名后入口失效或误显。
 * 归一化去开头斜杠比较，兼容后端 path 带/不带 `/` 前缀两种写法。
 */
const hasSystemMenu = computed(() =>
  permissionStore.topbarRouters?.some((route) => (route.path || '').replace(/^\/+/, '') === 'system')
);
const showNotify = ref(false);
const notifyUnreadCount = ref(0);
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
const { proxy } = getCurrentInstance();
let noticeSocketObj: any = null
// const menuMap = ref([
//   {
//     name:t('menuList.Operational'),
//     router:'/workCenter'
//   },
//   {
//     name:t('menuList.system'),
//     router:'/system'
//   }
// ])
// function normalizeUrl(url) {
//   return url.replace(/([^:]\/)\/+/g, '$1');
// }
// const menuArr = computed(()=>{
//   let arr = []
//   menuMap.value.forEach(item=>{
//     let routeObj = routers.value.find(ite=>ite.path==item.router)
//     if(routeObj && routeObj?.children?.length>0){
//       let routeUrl = ''
//       if(routeObj.children[0].parentPath){
//         routeUrl = routeObj.children[0].path
//       }else{
//         routeUrl = normalizeUrl(routeObj.path + '/' + routeObj.children[0].path)
//       }
//       arr.push({
//         ...item,
//         route:routeUrl
//       })
//     }
//   })
//   return arr
// })
/**
 * 顶部导航栏配置。
 * - `toolbarOnly`：仅渲染右侧工具区，供大屏等自定义标题布局复用
 * - `showFullscreenButton`：在消息图标左侧显示全屏切换
 * - `showNotification`：控制消息通知图标与通知面板，大屏等场景可关闭
 * - `lightToolbarIcons`：右侧工具栏图标使用白色填充，仅大屏等场景开启
 * - `showUserMenu`：控制头像、个人中心与退出登录入口，便于大屏隐藏用户操作
 */
const props = defineProps({
  /** 是否显示底部边框 */
  isBottomBorder: {
    type: Boolean,
    default: true
  },
  /** 是否为首页布局（100px 高度） */
  isHome: {
    type: Boolean,
    default: false
  },
  /** 是否显示跳转城市治理大屏按钮 */
  showDpButton: {
    type: Boolean,
    default: true
  },
  /** 是否在消息左侧显示全屏按钮 */
  showFullscreenButton: {
    type: Boolean,
    default: false
  },
  /** 是否显示消息通知图标与通知面板 */
  showNotification: {
    type: Boolean,
    default: true
  },
  /** 右侧工具栏图标是否使用白色（大屏场景） */
  lightToolbarIcons: {
    type: Boolean,
    default: false
  },
  /** 仅渲染右侧工具栏（时间、语言、消息、头像等） */
  toolbarOnly: {
    type: Boolean,
    default: false
  },
  /** 是否显示头像及其用户操作菜单 */
  showUserMenu: {
    type: Boolean,
    default: true
  }
});
const goBigScreen = ()=>{
   const routeUrl = proxy.$router.resolve({ path: '/governance/big-screen' }).href;
    window.open(routeUrl, '_blank');
}
const borderStyle = computed(() => ({ borderBottom: props.isBottomBorder ? '1px solid #2a2c33' : 'none' })), // 底部边框样式
  homeHeightStyle = computed(() => ({ height: props.isHome ? '100px' : '80px' })); // 顶部header栏高度

/** 工具栏图标默认色；大屏 lightToolbarIcons 时使用纯白 */
const TOOLBAR_ICON_COLOR = '#929bb2';
const TOOLBAR_ICON_COLOR_LIGHT = '#ffffff';

/**
 * 右侧工具栏图标样式，大屏场景统一白色填充。
 *
 * @param size 图标字号（px）
 */
const toolbarIconStyle = (size: number) => ({
  color: props.lightToolbarIcons ? TOOLBAR_ICON_COLOR_LIGHT : TOOLBAR_ICON_COLOR,
  fontSize: `${size}px`,
  cursor: 'pointer'
});

/**
 * 接收通知面板同步的全部未读数量。
 * 顶部消息图标只负责红点显隐，不重复读取 localStorage，避免父子组件各自计算导致状态不一致。
 *
 * @param count 通知面板计算出的全部未读数量
 */
const handleNotifyUnreadChange = (count: number) => {
  notifyUnreadCount.value = Number(count) || 0;
};

const currentTime = ref(parseTime(new Date(), '{h}:{i}:{s}'));
let timeObj = null;
onMounted(() => {
  timeObj = setInterval(() => {
    currentTime.value = parseTime(new Date(), '{h}:{i}:{s}');
  }, 1000);
  if (props.showNotification) {
    // 顶部栏是全局常驻入口，只在这里监听 /global/notice，再通过事件总线分发，避免通知面板和业务页面重复建立长连接。
    noticeSocketObj = initWebSocket('/global/notice',{
      onMessage:(obj,ev)=>{
        const notice = normalizeNoticePayload(ev?.data || obj);
        if (notice) emitter.emit(EVENT_GLOBAL_NOTICE_RECEIVED, notice);
      }
    })
  }
});
onBeforeUnmount(() => {
  timeObj && clearInterval(timeObj);
  timeObj = null;
  // 组件卸载时主动关闭连接，避免布局切换或大屏复用顶部栏时残留重复 socket 监听。
  noticeSocketObj?.close?.();
  noticeSocketObj = null;
});
/**
 * 注销当前用户并跳转到携带当前租户 ID 的登录页。
 * @returns {Promise<void>} 注销接口完成并触发整页跳转后结束
 * @sideeffect 清除用户登录态，并通过 location.href 重新加载登录页
 */
const logout = async () => {
  await ElMessageBox.confirm(t('navbar.logoutConfirmMessage'), t('navbar.logoutConfirmTitle'), {
    confirmButtonText: t('navbar.confirm'),
    cancelButtonText: t('navbar.cancel'),
    type: 'warning',
    customClass: 'night-message-box'
  });
  // 退出前保存 Store 中的租户 ID，登录页加载后会从 redirect 解析并更新本地租户缓存。
  const logoutTenantId = userStore.tenantId;
  await userStore.logout();
  location.href = getTenantLoginUrl(logoutTenantId);
};
const profile = async () => {
  showProfile.value = true
};
// 定义Command方法对象 通过key直接调用方法
const commandMap: { [key: string]: any } = {
  logout,
  profile
};
const handleCommand = (command: string) => {
  // 判断是否存在该方法
  if (commandMap[command]) {
    commandMap[command]();
  }
};
</script>
<style lang="scss" scoped>
.top_header_con {
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: aliceblue;

  .left {
    height: 100%;
    display: flex;
    align-items: center;
  }

  &--toolbar-only {
    width: auto;
    justify-content: flex-end;
  }

  &--with-fullscreen .right {
    width: 300px;
  }

  &--light-icons {
    :deep(.lang-dropdown-btn.is-light) {
      color: #fff;
    }
  }

  .top-header-fullscreen {
    flex-shrink: 0;
    font-size: 22px;
    color: #929bb2;
    cursor: pointer;
  }

  &--light-icons .top-header-fullscreen {
    color: #fff;
  }

  .right {
    flex-shrink: 0;
    width: 260px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    margin-right: 20px;
    // background-color: #181b1e;
    // border: 1px solid #46515f;
    // border-radius: 46px;
    font-family: HarmonyOS Sans SC;

    .time {
      // color: #0d83fc;
      font-weight: 700;
      font-size: 20px;
      letter-spacing: 1px;
    }
  }
}
</style>
